import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { 
  Globe, 
  Books, 
  CheckCircle, 
  Lightbulb, 
  Headset, 
  ChatCircleText,
  ArrowRight
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
  >
    <Card className="card-default hover:shadow-md hover:border-indigo-100 transition-all duration-300">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500 mb-1">{label}</p>
            <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {value}
            </p>
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
            <Icon className="w-6 h-6" weight="duotone" />
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

export const DashboardPage = ({ user, setUser }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/admin/stats`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const quickActions = [
    { 
      label: "Tilføj site", 
      icon: Globe, 
      path: "/sites",
      description: "Konfigurer et nyt site"
    },
    { 
      label: "Opret artikel", 
      icon: Books, 
      path: "/knowledge-base/new",
      description: "Tilføj til vidensbasen"
    },
    { 
      label: "Se forslag", 
      icon: Lightbulb, 
      path: "/suggestions",
      description: "Gennemgå Zendesk forslag"
    },
    { 
      label: "Test widget", 
      icon: ChatCircleText, 
      path: "/widget-preview",
      description: "Preview chat widget"
    },
  ];

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-6 md:p-8" data-testid="dashboard-page">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 
            className="text-3xl font-bold text-slate-900 mb-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Velkommen, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-slate-600">
            Her er et overblik over din AI kundeservice platform
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
          <StatCard 
            icon={Globe} 
            label="Aktive sites" 
            value={loading ? "..." : stats?.sites_count || 0}
            color="bg-indigo-50 text-indigo-600"
            delay={0}
          />
          <StatCard 
            icon={Books} 
            label="Artikler i alt" 
            value={loading ? "..." : stats?.articles_count || 0}
            color="bg-blue-50 text-blue-600"
            delay={0.05}
          />
          <StatCard 
            icon={CheckCircle} 
            label="Godkendte" 
            value={loading ? "..." : stats?.approved_articles || 0}
            color="bg-emerald-50 text-emerald-600"
            delay={0.1}
          />
          <StatCard 
            icon={Lightbulb} 
            label="Afventende forslag" 
            value={loading ? "..." : stats?.pending_suggestions || 0}
            color="bg-amber-50 text-amber-600"
            delay={0.15}
          />
          <StatCard 
            icon={Headset} 
            label="Åbne handoffs" 
            value={loading ? "..." : stats?.pending_handoffs || 0}
            color="bg-rose-50 text-rose-600"
            delay={0.2}
          />
          <StatCard 
            icon={ChatCircleText} 
            label="Chats (7 dage)" 
            value={loading ? "..." : stats?.recent_chats || 0}
            color="bg-violet-50 text-violet-600"
            delay={0.25}
          />
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 
            className="text-xl font-semibold text-slate-900 mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Hurtige handlinger
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action, index) => (
              <Card 
                key={action.path}
                className="card-default hover:shadow-md hover:border-indigo-100 transition-all duration-300 cursor-pointer group"
                onClick={() => navigate(action.path)}
                data-testid={`quick-action-${action.path.replace('/', '')}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 transition-colors">
                      <action.icon className="w-5 h-5 text-indigo-600" weight="duotone" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{action.label}</h3>
                  <p className="text-sm text-slate-500">{action.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.div>

        {/* Getting Started Guide (show if no sites) */}
        {!loading && stats?.sites_count === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8"
          >
            <Card className="card-default border-indigo-200 bg-indigo-50/30">
              <CardHeader>
                <CardTitle 
                  className="text-lg font-semibold text-slate-900"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  Kom i gang
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900">Opret dit første site</h4>
                    <p className="text-sm text-slate-600">Konfigurer gardinshoppen.dk eller et andet domæne</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900">Tilføj vidensbase artikler</h4>
                    <p className="text-sm text-slate-600">Opret FAQ'er og svar som chatbotten kan bruge</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900">Integrer chat widget</h4>
                    <p className="text-sm text-slate-600">Kopier embed koden til dit website</p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/sites')}
                  className="btn-primary mt-2"
                  data-testid="get-started-btn"
                >
                  Opret første site
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </Layout>
  );
};
